/**
 * The services-catalogue → job-form hand-off (ticket #5616).
 *
 * The post-job flow already knows how to open pre-answered: `decideInitialValues` restores a
 * category's answers, questions-step history and last step from the `job-form` localStorage
 * entry, and hands them to the form as its initial values. So a catalogue row click does not
 * need a new vehicle — it writes that very entry, in the exact shape `saveToLocalStorageEffect`
 * writes it, and the existing restore path does the rest.
 *
 * An existing entry for the category is merged rather than replaced, so a location/address the
 * user already gave survives, and the row's answers layer on top of whatever was stored.
 */

import { PostJobSteps } from 'Douleutaras/views/job/post-job/hooks/post-job';

import { CatalogueSkip, PreAnsweredQuestions } from './catalogue-skip';

/** The localStorage key the post-job form saves to and restores from. */
export const JOB_FORM_STORAGE_KEY = 'job-form';

/** One category's stored form state, as written by `saveToLocalStorageEffect`. */
export interface StoredCategoryJobForm {
  form: { questions: PreAnsweredQuestions };
  questionsStepHistory: Array<number>;
  currentStep: PostJobSteps;
  location?: string;
  address?: string;
}

/** The whole entry: one stored form per category id. */
export type StoredJobForm = Record<string, StoredCategoryJobForm>;

/** Reads the stored entry, tolerating absent, unparseable or off-shape content. */
export const readStoredJobForm = (): StoredJobForm => {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    const raw = JSON.parse(localStorage.getItem(JOB_FORM_STORAGE_KEY));

    return typeof raw === 'object' && Boolean(raw) && !Array.isArray(raw) ? raw as StoredJobForm : {};
  } catch {
    return {};
  }
};

/**
 * Merges a resolved catalogue skip into the stored form of `categoryId`: the pre-answers land in
 * `form.questions` and their questions-step history in `questionsStepHistory`, which is what
 * makes the form open past those questions with the answers already applied.
 *
 * Returns the merged entry (exported for the test), or `undefined` when there is nothing to
 * store — a row with no resolvable ids leaves storage untouched, so the form only skips the
 * category selection it was opened with.
 */
export const mergeCatalogueSkipIntoJobForm = (
  stored: StoredJobForm,
  categoryId: string,
  skip: CatalogueSkip
): StoredCategoryJobForm | undefined => {
  if (skip.history.length === 0) {
    return undefined;
  }

  const existing = stored[categoryId];
  // Union, keeping the stored order: a question already visited must not be listed twice.
  const history = [
    ...(existing?.questionsStepHistory ?? []),
    ...skip.history.filter(id => !existing?.questionsStepHistory?.includes(id))
  ];

  return {
    ...existing,
    form: { ...existing?.form, questions: { ...existing?.form?.questions, ...skip.questions }},
    questionsStepHistory: history,
    currentStep: PostJobSteps.QUESTIONS_STEP
  };
};

/** Writes the merged entry for `categoryId`, leaving every other category's entry alone. */
export const storeCatalogueJobForm = (categoryId: string, skip: CatalogueSkip): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const stored = readStoredJobForm();
  const merged = mergeCatalogueSkipIntoJobForm(stored, categoryId, skip);

  if (!merged) {
    return;
  }

  localStorage.setItem(JOB_FORM_STORAGE_KEY, JSON.stringify({ ...stored, [categoryId]: merged }));
};
